import React, { useEffect, useState } from "react";
import { db, storage } from "../../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import "./admin.css";

interface Legislation {
  id: string;
  title: string;
  description: string;
  pdf_url: string;
  category?: string; // optional, used for grouping
}

export const JusticeAdminPage = () => {
  const [headerImage, setHeaderImage] = useState("");
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [legislations, setLegislations] = useState<Legislation[]>([]);
  const [newLaw, setNewLaw] = useState<Partial<Legislation>>({});
  const [saving, setSaving] = useState(false);

  // -------- LOAD EXISTING DATA --------
  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, "site_content", "justice_page");
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data() as any;

        setHeaderImage(data.header_image || "");

        const list: Legislation[] = (data.legislations || []).map(
          (item: any, index: number) => ({
            id: item.id || `law-${index}`,
            title: item.title || "",
            description: item.description || "",
            pdf_url: item.pdf_url || "",
            category: item.category || "",
          })
        );

        setLegislations(list);
      }
    };
    fetchData();
  }, []);

  // -------- HEADER IMAGE --------
  const handleHeaderUpload = async () => {
    if (!headerFile) return;
    const storageRef = ref(storage, `justice/header_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, headerFile);

    uploadTask.on("state_changed", undefined, console.error, async () => {
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      setHeaderImage(url);
    });
  };

  // -------- PDF UPLOAD (REUSED) --------
  const handlePdfUpload = async (file: File) => {
    const storageRef = ref(storage, `justice/pdf_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        undefined,
        reject,
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });
  };

  // -------- EDIT EXISTING LAW --------
  const updateLawField = (
    id: string,
    field: keyof Legislation,
    value: string
  ) => {
    setLegislations(prev =>
      prev.map(law =>
        law.id === id ? { ...law, [field]: value } : law
      )
    );
  };

  const replaceLawPdf = async (id: string, file: File) => {
    const url = await handlePdfUpload(file);
    setLegislations(prev =>
      prev.map(law =>
        law.id === id ? { ...law, pdf_url: url } : law
      )
    );
  };

  // -------- ADD NEW LAW --------
  const addNewLegislation = async () => {
    if (!newLaw.title || !newLaw.description || !newLaw.pdf_url) return;

    const law: Legislation = {
      id: uuidv4(),
      title: newLaw.title,
      description: newLaw.description,
      pdf_url: newLaw.pdf_url,
      category: newLaw.category?.trim() || "",
    };

    setLegislations(prev => [...prev, law]);
    setNewLaw({});
  };

  // -------- DELETE LAW --------
  const deleteLegislation = (id: string) => {
    setLegislations(prev => prev.filter(law => law.id !== id));
  };

  // -------- SAVE ALL --------
  const saveAll = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "site_content", "justice_page"), {
        header_image: headerImage,
        legislations,
      });
      alert("Salvat cu succes!");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-container">
      <h1>Administrare Pagina Justiție</h1>

      {/* HEADER IMAGE */}
      <div className="admin-block">
        <label>Imagine Header:</label>
        <input
          type="file"
          accept="image/*"
          onChange={e => setHeaderFile(e.target.files?.[0] || null)}
        />
        <button onClick={handleHeaderUpload}>Încarcă imagine</button>
        {headerImage && (
          <img
            src={headerImage}
            alt="Header"
            className="preview-img"
          />
        )}
      </div>

      <hr />

      {/* EXISTING LAWS (NOW EDITABLE) */}
      <h2>Legislații existente</h2>
      {legislations.length === 0 && (
        <p>Nu există documente încă.</p>
      )}

      {legislations.map(law => (
        <div key={law.id} className="law-item">
          <input
            type="text"
            className="law-input-title"
            value={law.title}
            placeholder="Titlu"
            onChange={e =>
              updateLawField(law.id, "title", e.target.value)
            }
          />

          <input
            type="text"
            className="law-input-category"
            value={law.category || ""}
            placeholder="Categorie (ex: Legi, CCM, Contracte)"
            onChange={e =>
              updateLawField(law.id, "category", e.target.value)
            }
          />

          <textarea
            className="law-input-description"
            value={law.description}
            placeholder="Descriere"
            onChange={e =>
              updateLawField(law.id, "description", e.target.value)
            }
          />

          <div className="law-file-row">
            {law.pdf_url && (
              <a
                href={law.pdf_url}
                target="_blank"
                rel="noreferrer"
              >
                Vezi PDF
              </a>
            )}

            <input
              type="file"
              accept="application/pdf"
              onChange={async e => {
                const file = e.target.files?.[0];
                if (file) {
                  await replaceLawPdf(law.id, file);
                }
              }}
            />
          </div>

          <button
            className="delete-btn"
            onClick={() => deleteLegislation(law.id)}
          >
            Șterge
          </button>
        </div>
      ))}

      <hr />

      {/* ADD NEW LAW */}
      <h2>Adaugă legislație nouă</h2>
      <input
        type="text"
        placeholder="Titlu"
        value={newLaw.title || ""}
        onChange={e =>
          setNewLaw(prev => ({ ...prev, title: e.target.value }))
        }
      />
      <textarea
        placeholder="Descriere"
        value={newLaw.description || ""}
        onChange={e =>
          setNewLaw(prev => ({
            ...prev,
            description: e.target.value,
          }))
        }
      />
      <input
        type="text"
        placeholder="Categorie (ex: Legi, CCM, Contracte, Proceduri)"
        value={newLaw.category || ""}
        onChange={e =>
          setNewLaw(prev => ({
            ...prev,
            category: e.target.value,
          }))
        }
      />
      <input
        type="file"
        accept="application/pdf"
        onChange={async e => {
          const file = e.target.files?.[0];
          if (file) {
            const url = await handlePdfUpload(file);
            setNewLaw(prev => ({ ...prev, pdf_url: url }));
          }
        }}
      />
      <button onClick={addNewLegislation}>
        Adaugă
      </button>

      <hr />

      <button
        onClick={saveAll}
        className="save-btn"
        disabled={saving}
      >
        {saving ? "Se salvează..." : "Salvează tot"}
      </button>
    </div>
  );
};